export type DeviceMode = "desktop" | "mobile" | string | undefined;

export type GroupType = "padding" | "border" | "shadow";

export interface SpacingValues {
  top?: number | string;
  right?: number | string;
  bottom?: number | string;
  left?: number | string;
}

export interface BorderValues {
  width?: number | string;
  style?: string;
  color?: string;
  radius?: number | string;
}

export interface ShadowValues {
  x?: number | string;
  y?: number | string;
  blur?: number | string;
  spread?: number | string;
  color?: string;
}

export interface GroupDefaults {
  padding: SpacingValues;
  border: BorderValues;
  shadow: ShadowValues;
}

export interface DeviceAwareObject {
  padding?: SpacingValues;
  mobilePadding?: SpacingValues;
  border?: BorderValues;
  mobileBorder?: BorderValues;
  shadow?: ShadowValues;
  mobileShadow?: ShadowValues;
  [key: string]: unknown;
}

/**
 * @description Checks if the current device mode is mobile
 * @param deviceMode - The current device mode
 * @returns True if device mode is mobile, false otherwise
 */
export const isMobileDevice = (deviceMode: DeviceMode): boolean =>
  String(deviceMode || "desktop") === "mobile";

/**
 * @description Ensures mobile defaults are set on an object if not already present
 * @param obj - The object to ensure defaults on
 * @param defaults - The default values to apply
 */
const ensureMobileDefaultsInternal = (obj: DeviceAwareObject, defaults: GroupDefaults): void => {
  if (!obj) {
    return;
  }
  if (!obj.mobilePadding) {
    obj.mobilePadding = { ...defaults.padding };
  }
  if (!obj.mobileBorder) {
    obj.mobileBorder = { ...defaults.border };
  }
  if (!obj.mobileShadow) {
    obj.mobileShadow = { ...defaults.shadow };
  }
};

/**
 * @description Gets the active style group based on device mode
 * @param obj - The object containing style groups
 * @param group - The group type to retrieve (padding, border, or shadow)
 * @param isMobile - Whether to get mobile-specific values
 * @returns The active style group values
 */
export const getActiveGroup = (
  obj: DeviceAwareObject | null | undefined,
  group: GroupType,
  isMobile: boolean
): SpacingValues | BorderValues | ShadowValues => {
  if (!obj) {
    return {};
  }
  if (isMobile) {
    const mobileKey = `mobile${group[0].toUpperCase()}${group.slice(1)}` as keyof DeviceAwareObject;
    return (obj[mobileKey] as SpacingValues | BorderValues | ShadowValues) || obj[group] || {};
  }
  return obj[group] || {};
};

/**
 * @description Sets a value in the active style group based on device mode
 * @param obj - The object containing style groups
 * @param group - The group type to modify (padding, border, or shadow)
 * @param key - The specific property key to set
 * @param value - The value to set
 * @param isMobile - Whether to set mobile-specific values
 * @param defaults - Optional defaults to apply for mobile if not present
 */
export const setActiveGroupValue = (
  obj: DeviceAwareObject | null | undefined,
  group: GroupType,
  key: string,
  value: string | number,
  isMobile: boolean,
  defaults?: GroupDefaults
): void => {
  if (!obj) {
    return;
  }
  if (isMobile) {
    if (defaults) {
      ensureMobileDefaultsInternal(obj, defaults);
    }
    const mobileKey = `mobile${group[0].toUpperCase()}${group.slice(1)}` as keyof DeviceAwareObject;
    if (!obj[mobileKey]) {
      (obj[mobileKey] as Record<string, unknown>) = {};
    }
    (obj[mobileKey] as Record<string, unknown>)[key] = value;
    return;
  }
  if (!obj[group]) {
    (obj[group] as Record<string, unknown>) = {};
  }
  (obj[group] as Record<string, unknown>)[key] = value;
};

/**
 * @description Gets a device-aware property value, falling back to desktop if mobile not set
 * @param obj - The object containing the properties
 * @param desktopKey - The key for the desktop property
 * @param mobileKey - The key for the mobile property
 * @param isMobile - Whether to prefer mobile values
 * @returns The appropriate value based on device mode
 */
export const getDeviceAware = <T>(
  obj: Record<string, unknown> | null | undefined,
  desktopKey: string,
  mobileKey: string,
  isMobile: boolean
): T | undefined => {
  if (!obj) {
    return undefined;
  }
  return isMobile ? (obj[mobileKey] as T) ?? (obj[desktopKey] as T) : (obj[desktopKey] as T);
};

/**
 * @description Sets a device-aware property value
 * @param obj - The object to set the property on
 * @param desktopKey - The key for the desktop property
 * @param mobileKey - The key for the mobile property
 * @param value - The value to set
 * @param isMobile - Whether to set the mobile property
 */
export const setDeviceAware = <T>(
  obj: Record<string, unknown> | null | undefined,
  desktopKey: string,
  mobileKey: string,
  value: T,
  isMobile: boolean
): void => {
  if (!obj) {
    return;
  }
  if (isMobile) {
    obj[mobileKey] = value;
  } else {
    obj[desktopKey] = value;
  }
};
